import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, post } from '@truefit/http-utils';
import useCurrentGameId from '@/shared/hooks/useCurrentGameId';
import { FramingPreset } from '../types';
import { Vec3 } from '../geometry';

// Not under ['Games', gameId, 'Cameras']: that is the camera list's own key,
// and refetching it re-aims the preview mid-move.
const queryKey = (gameId: string, cameraId?: number) => ['FramingPresets', gameId, cameraId];

/** The caller's framing presets, aimed from one camera of the current game. */
export const useFramingPresetsQuery = (cameraId?: number) => {
  const gameId = useCurrentGameId();
  return useQuery({
    queryKey: queryKey(gameId, cameraId),
    enabled: !!gameId && cameraId != null,
    queryFn: async () => {
      const response = await get<{ presets: FramingPreset[] }>(
        `/v1.0/games/${gameId}/cameras/${cameraId}/framing-presets`,
      );
      return response.data.presets;
    },
  });
};

export const useCreateFramingPresetMutation = () => {
  const queryClient = useQueryClient();
  const gameId = useCurrentGameId();
  return useMutation({
    mutationFn: async ({
      cameraId,
      name,
      target,
    }: {
      cameraId: number;
      name: string;
      target: Vec3;
    }) => {
      const response = await post<{ preset: FramingPreset }>(
        `/v1.0/games/${gameId}/cameras/${cameraId}/framing-presets`,
        { name, ...target },
      );
      return response.data.preset;
    },
    // Every camera's list carries every preset, so refresh them all.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['FramingPresets', gameId] }),
  });
};

export const useDeleteFramingPresetMutation = () => {
  const queryClient = useQueryClient();
  const gameId = useCurrentGameId();
  return useMutation({
    mutationFn: async (presetUniqueId: string) => {
      await del(`/v1.0/framing-presets/${presetUniqueId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['FramingPresets', gameId] }),
  });
};
